import { useState, useMemo } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { getCityOptions, getCityLabel, CityEnum } from "@/utils/cities";

interface CitySelectorProps {
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function CitySelector({
  value,
  onValueChange,
  placeholder = "Select city...",
  disabled = false,
  className,
}: CitySelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const cityOptions = useMemo(() => getCityOptions(), []);
  const selectedCity = value ? getCityLabel(value as CityEnum) : null;

  // Filter cities based on search term
  const filteredCities =
    searchTerm.trim() === ""
      ? cityOptions
      : cityOptions.filter((city) =>
          city.label.toLowerCase().includes(searchTerm.toLowerCase()),
        );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between border-secondary", className)}
          disabled={disabled}
        >
          {selectedCity ? selectedCity : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-2">
        <div className="space-y-2">
          <input
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            placeholder="Search cities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {filteredCities.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No cities found.
            </div>
          ) : (
            <div className="max-h-[300px] overflow-y-auto">
              {filteredCities.map((city) => (
                <div
                  key={city.value}
                  className={cn(
                    "flex items-center px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent hover:text-accent-foreground",
                    value === city.value && "bg-accent text-accent-foreground",
                  )}
                  onClick={() => {
                    onValueChange(value === city.value ? "" : city.value);
                    setOpen(false);
                    setSearchTerm(""); // Clear search when selecting
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === city.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span>{city.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
